import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import i18n from "../../../config/i18n";
import { sharedApi, getAuthToken } from "../../../services/sharedApi";
import { speechService } from "../../../services/speechService";
import { DictationStatus } from "../../../types/dictation";
import { toApiError } from "../../../utils/errorUtils";
import { logger } from "../../../utils/logger";
// (Will import RootState/AppDispatch when refactoring dispatch signatures further)
import {
  normalizeSentenceForComparison,
  normalizeWordForComparison,
  tokenizeText,
} from "../../../utils/textNormalization";
import { dictationService } from "../services/dictationService";

import type { DictationDraft } from "./draftStorage";
import type {
  DictationState,
  DictationSessionRequest,
  DictationSubmissionRequest,
  DictationSettings,
} from "../../../types/dictation";
import type {
  DictationRecommendation,
  DictationRecommendationRequest,
} from "@lingoshade/domain";
import type { PayloadAction } from "@reduxjs/toolkit";

// To avoid storing non-serializable data in Redux state, use module variables
let sessionAudioBuffer: ArrayBuffer | null = null;
let sessionWordBoundaries: Array<{
  word: string;
  offset: number;
  duration: number;
}> | null = null;
let sessionAudioContext: AudioContext | null = null;
let sessionAudioSource: AudioBufferSourceNode | null = null;

// Flag to track AudioContext initialization on iOS
let isAudioContextInitialized = false;

// Fallback audio element for iOS
let fallbackAudioElement: HTMLAudioElement | null = null;
let fallbackAudioBlob: string | null = null;
let fallbackTimeoutId: NodeJS.Timeout | null = null;
let audioVersion = 0;

/** Release the previous exercise and invalidate audio requests still in flight. */
export function clearDictationAudio(): void {
  audioVersion += 1;
  if (sessionAudioSource) {
    try {
      sessionAudioSource.stop();
      sessionAudioSource.disconnect();
    } catch (error: unknown) {
      logger.debug("Dictation audio source already stopped", error);
    }
    sessionAudioSource = null;
  }
  fallbackAudioElement?.pause();
  fallbackAudioElement = null;
  if (fallbackAudioBlob) URL.revokeObjectURL(fallbackAudioBlob);
  fallbackAudioBlob = null;
  if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
  fallbackTimeoutId = null;
  sessionAudioBuffer = null;
  sessionWordBoundaries = null;
  currentWordBoundaryIndex = 0;
}

// Function to detect iOS and check if we need fallback
function shouldUseFallbackAudio(): boolean {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const needsFallback = isIOS || (isSafari && !isAudioContextInitialized);

  logger.debug("[shouldUseFallbackAudio] device detection", {
    userAgent: navigator.userAgent,
    isIOS,
    isSafari,
    isAudioContextInitialized,
    needsFallback,
  });

  return needsFallback;
}

// Function to create fallback audio element
function createFallbackAudio(): void {
  if (fallbackAudioElement || !sessionAudioBuffer) return;

  try {
    // Convert ArrayBuffer to Blob and create URL
    const audioBlob = new Blob([sessionAudioBuffer], { type: "audio/wav" });
    fallbackAudioBlob = URL.createObjectURL(audioBlob);

    // Create audio element
    fallbackAudioElement = new Audio(fallbackAudioBlob);
    fallbackAudioElement.preload = "auto";

    logger.debug("Fallback audio element created for iOS");
  } catch (error) {
    logger.error("Failed to create fallback audio", error);
  }
}

// Initialize AudioContext for iOS compatibility (requires user interaction)
function initializeAudioContextOnUserInteraction(): AudioContext {
  logger.debug(
    "[initializeAudioContextOnUserInteraction] Initializing AudioContext...",
  );

  if (!sessionAudioContext) {
    const w = window as typeof window & {
      webkitAudioContext?: typeof AudioContext;
    };
    const Ctor = w.AudioContext || w.webkitAudioContext;
    sessionAudioContext = new Ctor();
    logger.debug("AudioContext created", { state: sessionAudioContext.state });
  }

  // For iOS: attempt to resume context immediately on user interaction
  if (sessionAudioContext.state === "suspended") {
    logger.debug("AudioContext suspended, trying to resume...");
    sessionAudioContext
      .resume()
      .then(() => {
        logger.debug("AudioContext resumed synchronously on interaction");
        isAudioContextInitialized = true;
      })
      .catch((error) => {
        logger.error("Error resuming AudioContext synchronously", error);
      });
  } else if (sessionAudioContext.state === "running") {
    isAudioContextInitialized = true;
    logger.debug("AudioContext already active");
  }

  return sessionAudioContext;
}

// Helper functions for audio processing
function findSentenceTimings(
  sentence: string,
  wordBoundaries: Array<{ word: string; offset: number; duration: number }>,
): { start: number; end: number } | null {
  logger.debug("[findSentenceTimings] analyzing sentence", {
    sentence,
    currentSearchIndex: currentWordBoundaryIndex,
  });

  if (!wordBoundaries || wordBoundaries.length === 0) {
    logger.warn("No wordBoundaries available for sentence timings");
    return null;
  }

  // Detect if this is a CJK language
  const isCJK = /[\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/.test(
    sentence,
  );
  logger.debug("Is CJK language", { isCJK });

  if (isCJK) {
    return findCJKSentenceTimings(sentence, wordBoundaries);
  }

  // Original logic for non-CJK languages
  const cleanSentence = normalizeSentenceForComparison(sentence);
  const sentenceWords = tokenizeText(cleanSentence);

  if (sentenceWords.length === 0) {
    logger.warn("No words after normalization/tokenization");
    return null;
  }

  logger.debug("Sentence search context", {
    cleanSentence,
    words: sentenceWords,
    totalWordBoundaries: wordBoundaries.length,
    startIndex: currentWordBoundaryIndex,
  });

  // Show available words in search area
  const availableWords = wordBoundaries
    .slice(currentWordBoundaryIndex, currentWordBoundaryIndex + 15)
    .map(
      (b, i) => `${currentWordBoundaryIndex + i}: "${b.word}" (${b.offset}ms)`,
    );
  logger.debug("Available words in search area", availableWords);

  // Look for SEQUENCE of sentence words in wordBoundaries
  const foundSequence = findWordSequence(sentenceWords, wordBoundaries);

  if (!foundSequence || foundSequence.length === 0) {
    logger.error("Failed to find word sequence for sentence", { sentence });
    return null;
  }

  logger.debug("Found sequence indexes", foundSequence);

  const firstWordIndex = foundSequence[0];
  const lastWordIndex = foundSequence[foundSequence.length - 1];

  // Show found words
  const foundWordsInfo = foundSequence.map(
    (idx) =>
      `${idx}: "${wordBoundaries[idx].word}" (${wordBoundaries[idx].offset}ms)`,
  );
  logger.debug("Found words", foundWordsInfo);

  // Start time = beginning of first word
  const startTime = wordBoundaries[firstWordIndex].offset / 1000;

  // End time = end of last word, or beginning of next word (if exists)
  let endTime: number;
  if (lastWordIndex + 1 < wordBoundaries.length) {
    endTime = wordBoundaries[lastWordIndex + 1].offset / 1000;
    logger.debug("End time determined by start of next word");
  } else {
    endTime =
      (wordBoundaries[lastWordIndex].offset +
        wordBoundaries[lastWordIndex].duration) /
      1000;
    logger.debug("End time determined by end of last word");
  }

  // Add small pause at the beginning only for the first sentence
  let startWithPause: number;
  if (firstWordIndex === 0) {
    startWithPause = Math.max(0, startTime - 0.2);
    logger.debug("Added pause at beginning (first sentence)");
  } else {
    startWithPause = startTime;
    logger.debug("No pause at beginning (not first sentence)");
  }

  logger.debug("Sentence timing result", {
    start: startWithPause,
    end: endTime,
    matched: foundSequence.length,
    total: sentenceWords.length,
  });

  return { start: startWithPause, end: endTime };
}

// Special function for CJK sentence timing
function findCJKSentenceTimings(
  sentence: string,
  wordBoundaries: Array<{ word: string; offset: number; duration: number }>,
): { start: number; end: number } | null {
  logger.debug("[findCJKSentenceTimings] processing CJK sentence");

  const cleanSentence = normalizeSentenceForComparison(sentence);
  logger.debug("CJK sentence search context", {
    cleanSentence,
    startIndex: currentWordBoundaryIndex,
  });

  // Find words from wordBoundaries that appear in this sentence
  const matchingIndices: number[] = [];
  let searchPosition = 0; // Position within the sentence

  for (let i = currentWordBoundaryIndex; i < wordBoundaries.length; i++) {
    const boundary = wordBoundaries[i];
    const cleanWord = normalizeWordForComparison(boundary.word);

    // Try to find this word in the remaining part of the sentence
    const wordIndex = cleanSentence.indexOf(cleanWord, searchPosition);

    if (wordIndex !== -1) {
      logger.debug(
        `CJK match: "${boundary.word}" (clean: "${cleanWord}") at sentence position ${wordIndex}, boundary index ${i}`,
      );
      matchingIndices.push(i);
      searchPosition = wordIndex + cleanWord.length;
    } else {
      // If we can't find the word in the sentence, we've probably moved to the next sentence
      logger.debug(
        `CJK word "${boundary.word}" not found in sentence, stopping search`,
      );
      break;
    }
  }

  if (matchingIndices.length === 0) {
    logger.error("No matching words found for CJK sentence");
    return null;
  }

  const firstWordIndex = matchingIndices[0];
  const lastWordIndex = matchingIndices[matchingIndices.length - 1];

  logger.debug(
    `CJK found ${matchingIndices.length} words, range: ${firstWordIndex}-${lastWordIndex}`,
  );

  // Update search position for next sentence
  currentWordBoundaryIndex = lastWordIndex + 1;

  // Calculate timing
  const startTime = wordBoundaries[firstWordIndex].offset / 1000;
  let endTime: number;

  if (lastWordIndex + 1 < wordBoundaries.length) {
    endTime = wordBoundaries[lastWordIndex + 1].offset / 1000;
  } else {
    endTime =
      (wordBoundaries[lastWordIndex].offset +
        wordBoundaries[lastWordIndex].duration) /
      1000;
  }

  // Add pause for first sentence
  const startWithPause =
    firstWordIndex === 0 ? Math.max(0, startTime - 0.2) : startTime;

  logger.debug(
    `CJK timing result: ${startWithPause.toFixed(2)}s - ${endTime.toFixed(2)}s`,
  );
  return { start: startWithPause, end: endTime };
}

// Global variable to track the current search position through wordBoundaries
let currentWordBoundaryIndex = 0;

// Function to set correct search position based on sentence index
function setWordBoundaryPositionForSentence(
  sentenceIndex: number,
  sentences: string[],
  wordBoundaries: Array<{ word: string; offset: number; duration: number }>,
): void {
  logger.debug(
    `[setWordBoundaryPositionForSentence] set position for sentence ${sentenceIndex}`,
    { currentWordBoundaryIndex },
  );

  if (sentenceIndex === 0) {
    currentWordBoundaryIndex = 0;
    logger.debug("Search index reset to 0 (first sentence)");
    return;
  }

  // Check if this is a CJK language
  const isCJK = sentences.some((s) =>
    /[\u4e00-\u9fff\u3400-\u4dbf\u3040-\u309f\u30a0-\u30ff]/.test(s),
  );

  if (isCJK) {
    setCJKWordBoundaryPosition(sentenceIndex, sentences, wordBoundaries);
    return;
  }

  logger.debug("Analyzing previous sentences for search index", {
    count: sentenceIndex,
  });
  let searchIndex = 0;

  for (let i = 0; i < sentenceIndex; i++) {
    const sentence = sentences[i];
    logger.debug(`Sentence ${i}`, { sentence });

    const cleanSentence = normalizeSentenceForComparison(sentence);
    const sentenceWords = tokenizeText(cleanSentence);
    logger.debug("Sentence words", { index: i, words: sentenceWords });

    for (const targetWord of sentenceWords) {
      let found = false;
      for (
        let boundaryIndex = searchIndex;
        boundaryIndex < wordBoundaries.length;
        boundaryIndex++
      ) {
        const boundary = wordBoundaries[boundaryIndex];
        // Нормализуем слово из wordBoundaries используя утилиту
        const cleanBoundaryWord = normalizeWordForComparison(boundary.word);
        if (cleanBoundaryWord === targetWord) {
          logger.debug(
            `Word matched "${targetWord}" at position ${boundaryIndex}`,
          );
          searchIndex = boundaryIndex + 1;
          found = true;
          break;
        }
      }
      if (!found) {
        logger.warn(
          `Word "${targetWord}" from sentence ${i} not found in wordBoundaries`,
        );
        break;
      }
    }
    logger.debug(`After sentence ${i} searchIndex = ${searchIndex}`);
  }

  currentWordBoundaryIndex = searchIndex;
  logger.debug("Updated currentWordBoundaryIndex", {
    currentWordBoundaryIndex,
  });
}

// Special function for setting CJK word boundary position
function setCJKWordBoundaryPosition(
  sentenceIndex: number,
  sentences: string[],
  wordBoundaries: Array<{ word: string; offset: number; duration: number }>,
): void {
  logger.debug("[setCJKWordBoundaryPosition] processing CJK sentences");

  let searchIndex = 0;

  for (let i = 0; i < sentenceIndex; i++) {
    const sentence = sentences[i];
    const cleanSentence = normalizeSentenceForComparison(sentence);
    logger.debug(
      `CJK processing sentence ${i}: "${sentence}" (clean: "${cleanSentence}")`,
    );

    let sentencePosition = 0;
    let wordsFoundInSentence = 0;

    // Look for words from wordBoundaries that appear in this sentence
    for (
      let boundaryIndex = searchIndex;
      boundaryIndex < wordBoundaries.length;
      boundaryIndex++
    ) {
      const boundary = wordBoundaries[boundaryIndex];
      const cleanWord = normalizeWordForComparison(boundary.word);

      const wordIndex = cleanSentence.indexOf(cleanWord, sentencePosition);

      if (wordIndex !== -1) {
        logger.debug(
          `CJK word match "${boundary.word}" in sentence ${i} at position ${wordIndex}`,
        );
        sentencePosition = wordIndex + cleanWord.length;
        searchIndex = boundaryIndex + 1;
        wordsFoundInSentence++;
      } else {
        // Word not found in this sentence, probably belongs to next sentence
        break;
      }
    }

    logger.debug(
      `CJK sentence ${i} complete: found ${wordsFoundInSentence} words, next search starts at ${searchIndex}`,
    );
  }

  currentWordBoundaryIndex = searchIndex;
  logger.debug(`CJK final position set to: ${currentWordBoundaryIndex}`);
}

// Find sequence of words strictly following the audio order
function findWordSequence(
  sentenceWords: string[],
  wordBoundaries: Array<{ word: string; offset: number; duration: number }>,
): number[] | null {
  logger.debug("[findWordSequence] search sequence", {
    sentenceWords,
    startIndex: currentWordBoundaryIndex,
  });

  if (currentWordBoundaryIndex >= wordBoundaries.length) {
    logger.warn("Reached end of wordBoundaries (no more words)");
    return null;
  }

  const foundIndices: number[] = [];
  let searchIndex = currentWordBoundaryIndex;

  for (let wordIndex = 0; wordIndex < sentenceWords.length; wordIndex++) {
    const targetWord = sentenceWords[wordIndex];
    logger.debug(
      `Searching word "${targetWord}" starting from position ${searchIndex}`,
    );

    let wordFound = false;
    for (
      let boundaryIndex = searchIndex;
      boundaryIndex < wordBoundaries.length;
      boundaryIndex++
    ) {
      const boundary = wordBoundaries[boundaryIndex];
      // Нормализуем слово из wordBoundaries используя утилиту
      const cleanBoundaryWord = normalizeWordForComparison(boundary.word);

      logger.debug(
        `Compare "${targetWord}" with "${cleanBoundaryWord}" (original: "${boundary.word}")`,
      );

      if (cleanBoundaryWord === targetWord) {
        logger.debug(
          `Found "${targetWord}" at index ${boundaryIndex} (offset: ${boundary.offset}ms)`,
        );
        foundIndices.push(boundaryIndex);
        searchIndex = boundaryIndex + 1;
        wordFound = true;
        break;
      }
    }

    if (!wordFound) {
      logger.error(
        `Word "${targetWord}" NOT FOUND from position ${searchIndex} onward`,
      );
      logger.debug("Available words in search area:");
      const remainingWords = wordBoundaries
        .slice(searchIndex, searchIndex + 10)
        .map((b, i) => `  ${searchIndex + i}: "${b.word}"`);
      logger.debug(remainingWords.join("\n"));
      return null;
    }
  }

  if (foundIndices.length === sentenceWords.length) {
    currentWordBoundaryIndex = foundIndices[foundIndices.length - 1] + 1;
    logger.debug(
      `SUCCESS: all words found at positions [${foundIndices.join(", ")}] (next index: ${currentWordBoundaryIndex})`,
    );
    return foundIndices;
  }

  logger.error(
    `FAILURE: found only ${foundIndices.length}/${sentenceWords.length} words`,
  );
  return null;
}

function playAudioSegment(
  context: AudioContext,
  audioBuffer: AudioBuffer,
  timings: { start: number; end: number },
  _dispatch: (action: unknown) => unknown, // minimal callable; currently unused but reserved
): Promise<{ played: true }> {
  return new Promise((resolve, reject) => {
    logger.debug("[playAudioSegment] starting segment playback");

    // Check if we should use fallback audio for iOS
    if (shouldUseFallbackAudio()) {
      logger.debug("[playAudioSegment] Using fallback HTML5 audio for iOS");

      // Create fallback audio if not exists
      createFallbackAudio();

      if (fallbackAudioElement) {
        fallbackAudioElement.currentTime = timings.start;

        let hasEnded = false;

        const onEnded = (): void => {
          if (hasEnded) return;
          hasEnded = true;
          logger.debug("[playAudioSegment] Fallback playback completed");
          fallbackAudioElement?.removeEventListener("ended", onEnded);
          fallbackAudioElement?.removeEventListener("timeupdate", onTimeUpdate);
          if (fallbackTimeoutId) {
            clearTimeout(fallbackTimeoutId);
            fallbackTimeoutId = null;
          }
          resolve({ played: true });
        };
        const onTimeUpdate = (): void => {
          if (hasEnded || !fallbackAudioElement) return;

          // More precise stopping with small buffer
          const currentTime = fallbackAudioElement.currentTime;
          const targetEndTime = timings.end;

          // Stop at the exact target time with minimal buffer to prevent overlap
          if (currentTime >= targetEndTime - 0.01) {
            // 10ms before target for highest precision
            logger.debug(
              `[playAudioSegment] Stopping at ${currentTime.toFixed(3)}s (target: ${targetEndTime.toFixed(3)}s)`,
            );
            fallbackAudioElement.pause();
            onEnded();
          }
        };

        // Safety timeout to force stop at exact duration
        const duration = (timings.end - timings.start) * 1000; // Convert to milliseconds
        fallbackTimeoutId = setTimeout(
          () => {
            if (
              !hasEnded &&
              fallbackAudioElement &&
              !fallbackAudioElement.paused
            ) {
              logger.debug(
                "[playAudioSegment] Force stopping via timeout for precision",
              );
              fallbackAudioElement.pause();
              onEnded();
            }
          },
          Math.max(50, duration - 10),
        ); // Stop 10ms before calculated end for highest precision

        fallbackAudioElement.addEventListener("ended", onEnded);
        fallbackAudioElement.addEventListener("timeupdate", onTimeUpdate);

        // Start playback
        fallbackAudioElement
          .play()
          .then(() => {
            logger.debug(
              `[playAudioSegment] Fallback audio started at ${timings.start.toFixed(3)}s, stop at ${timings.end.toFixed(3)}s`,
            );
          })
          .catch((error): void => {
            logger.error("[playAudioSegment] Fallback audio failed", error);
            if (fallbackTimeoutId) {
              clearTimeout(fallbackTimeoutId);
              fallbackTimeoutId = null;
            }
            reject(new Error(i18n.t("dictation:errors.failedToPlaySentence")));
          });

        return;
      }
    }

    // Original Web Audio API implementation
    logger.debug("AudioContext state", context.state);
    logger.debug("AudioBuffer details", {
      sampleRate: audioBuffer.sampleRate,
      duration: audioBuffer.duration,
      numberOfChannels: audioBuffer.numberOfChannels,
      length: audioBuffer.length,
    });
    logger.debug("Timings", timings);

    const source = context.createBufferSource();
    // Keep reference to source for stopping playback
    sessionAudioSource = source;
    source.buffer = audioBuffer;
    source.connect(context.destination);

    const duration = timings.end - timings.start;

    source.onended = () => {
      logger.debug("[playAudioSegment] Segment playback completed");
      resolve({ played: true });
    };

    try {
      // Play only the needed segment
      logger.debug(
        `[playAudioSegment] Starting: start=${timings.start}s, duration=${duration}s`,
      );
      source.start(0, timings.start, duration);
      logger.debug(`[playAudioSegment] source.start() executed successfully`);
    } catch (error) {
      logger.error("[playAudioSegment] Error in source.start()", error);
      reject(new Error(i18n.t("dictation:errors.failedToPlaySentence")));
    }
  });
}

// Default settings
const defaultSettings: DictationSettings = {
  pauseBetweenWords: 0.8,
  pauseBetweenSentences: 3.0,
  playbackSpeed: 1.0,
  autoRepeat: false,
  timeoutPerSentence: 120,
};

// Initial state
const initialState: DictationState = {
  recommendationTextSize: "ALL",
  recommendation: null,
  recommendationLoading: false,
  recommendationError: null,
  recommendationRequestId: null,
  status: DictationStatus.IDLE,
  session: null,
  currentSentenceIndex: 0,
  userInput: "",
  userInputs: [], // вводы пользователя по предложениям
  sentenceRepeats: {},
  // Трекер, какие предложения уже воспроизводились
  playedSentences: {} as { [index: number]: boolean },
  startTime: null,
  result: null,
  error: null,
  settings: defaultSettings,
  stats: null,
  history: [],
  // Audio state
  volume: 1,
  currentTime: 0,
  audioDuration: 0,
  audioLoaded: false,
  hasStarted: false, // Изначально сессия не начата
  isPlayingAudio: false, // Изначально аудио не воспроизводится
};

export const loadDictationRecommendation = createAsyncThunk<
  DictationRecommendation | null,
  DictationRecommendationRequest | undefined,
  { rejectValue: string }
>(
  "dictation/recommendation",
  async (
    options,
    { rejectWithValue },
  ): Promise<
    DictationRecommendation | null | ReturnType<typeof rejectWithValue>
  > => {
    try {
      return await sharedApi.dictation.getRecommendation(
        options,
        getAuthToken(),
      );
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

// Async thunks
function extractErrorInfo(
  error: unknown,
  fallback: string,
): { status: number; data?: unknown; message: string } {
  if (typeof error === "object" && error !== null) {
    const maybe = error as {
      response?: { status?: number; data?: unknown };
      status?: number;
      message?: unknown;
    };
    const status =
      maybe.response?.status ||
      (typeof maybe.status === "number" ? maybe.status : 0);
    const data = maybe.response?.data;
    const rawMsg = (maybe as { message?: unknown }).message;
    const messageCandidate =
      (data && typeof data === "object" && "message" in data
        ? (data as { message?: unknown }).message
        : undefined) ||
      (data && typeof data === "object" && "error" in data
        ? (data as { error?: unknown }).error
        : undefined) ||
      rawMsg;
    if (typeof messageCandidate === "string") {
      return { status, data, message: messageCandidate };
    }
    return { status, data, message: fallback };
  }
  return { status: 0, data: undefined, message: fallback };
}

function classifyNetworkError(error: unknown): {
  isConnectionError: boolean;
  code?: string;
  status?: number;
  message?: string;
} {
  if (typeof error === "object" && error !== null) {
    const maybe = error as {
      code?: string;
      name?: string;
      message?: string;
      status?: number;
      response?: {
        status?: number;
        data?: { error?: string; message?: string };
      };
    };
    const status = maybe.response?.status || maybe.status;
    const message =
      maybe.response?.data?.error ||
      maybe.response?.data?.message ||
      maybe.message;
    const code = maybe.code;
    const isConn =
      code === "ECONNREFUSED" ||
      code === "ECONNRESET" ||
      maybe.name === "NetworkError" ||
      !!(status && status >= 500) ||
      (message && /network/i.test(message));
    return { isConnectionError: !!isConn, code, status, message };
  }
  return { isConnectionError: false };
}

export const startDictationSession = createAsyncThunk(
  "dictation/startSession",
  async (request: DictationSessionRequest = {}, { rejectWithValue }) => {
    try {
      return await dictationService.startSession(request);
    } catch (err: unknown) {
      const { status, data, message } = extractErrorInfo(
        err,
        i18n.t("dictation:errors.failedToStartDictationSession"),
      );
      return rejectWithValue({ status, data, message });
    }
  },
);

export const submitDictation = createAsyncThunk(
  "dictation/submit",
  async (
    submission: Omit<DictationSubmissionRequest, "wordBoundaries">,
  ): Promise<Awaited<ReturnType<typeof dictationService.submitDictation>>> => {
    // Include wordBoundaries from module-level variable
    const submissionWithBoundaries: DictationSubmissionRequest = {
      ...submission,
      wordBoundaries: sessionWordBoundaries || [],
    };

    logger.debug("[submitDictation] submitting payload summary", {
      sessionId: submissionWithBoundaries.sessionId,
      userInputLength: submissionWithBoundaries.userInput?.length || 0,
      userInputsBySentenceCount:
        submissionWithBoundaries.userInputsBySentence?.length || 0,
      timeSpentMs: submissionWithBoundaries.timeSpentMs,
      sentenceRepeatsCount: Object.keys(
        submissionWithBoundaries.sentenceRepeats || {},
      ).length,
      wordBoundariesCount: submissionWithBoundaries.wordBoundaries.length,
    });

    const result = await dictationService.submitDictation(
      submissionWithBoundaries,
    );

    return result;
  },
);

export const loadDictationStats = createAsyncThunk(
  "dictation/loadStats",
  async () => {
    return await dictationService.getStats();
  },
);

export const loadDictationHistory = createAsyncThunk(
  "dictation/loadHistory",
  async ({
    limit = 20,
    offset = 0,
  }: { limit?: number; offset?: number } = {}) => {
    return await dictationService.getHistory(limit, offset);
  },
);

export const loadDictationSettings = createAsyncThunk(
  "dictation/loadSettings",
  async () => {
    return await dictationService.getSettings();
  },
);

export const updateDictationSettings = createAsyncThunk(
  "dictation/updateSettings",
  async (settings: Partial<DictationSettings>) => {
    await dictationService.updateSettings(settings);
    return settings;
  },
);

export const loadSessionAudio = createAsyncThunk(
  "dictation/loadSessionAudio",
  async (_, { getState, rejectWithValue }) => {
    const requestAudioVersion = audioVersion;
    const maxRetries = 3;
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (requestAudioVersion !== audioVersion)
          return rejectWithValue(
            i18n.t("dictation:errors.dictationExerciseChanged"),
          );
        const state = getState() as { dictation: DictationState };
        const { session } = state.dictation;

        if (!session || !session.textId) {
          return rejectWithValue(
            i18n.t("dictation:errors.noActiveSessionOrTextid"),
          );
        }

        logger.debug(
          `Loading audio for session textId: ${session.textId} (attempt ${attempt}/${maxRetries})`,
        );

        // Загружаем аудио для всего текста
        const speechData = await speechService.synthesizeSpeech({
          textId: session.textId,
          voiceId: session.voiceId,
        });
        if (requestAudioVersion !== audioVersion)
          return rejectWithValue(
            i18n.t("dictation:errors.dictationExerciseChanged"),
          );

        logger.debug("Audio loaded successfully, converting to ArrayBuffer...");

        // Конвертируем base64 в ArrayBuffer
        const binaryString = atob(speechData.audio);
        const audioBuffer = new ArrayBuffer(binaryString.length);
        const uint8Array = new Uint8Array(audioBuffer);
        for (let i = 0; i < binaryString.length; i++) {
          uint8Array[i] = binaryString.charCodeAt(i);
        }

        // Save data to module variables (NOT in Redux state)
        sessionAudioBuffer = audioBuffer;
        sessionWordBoundaries = speechData.wordBoundaries || [];

        // Create fallback audio for iOS if needed
        if (shouldUseFallbackAudio()) {
          logger.debug("[loadSessionAudio] Creating fallback audio for iOS");
          createFallbackAudio();
        }

        // Log complete picture for diagnostics
        logger.debug("[loadSessionAudio] Audio session loaded", {
          audioSizeBytes: audioBuffer.byteLength,
          wordBoundaries: sessionWordBoundaries.length,
        });

        if (sessionWordBoundaries.length > 0) {
          logger.debug("First 10 wordBoundaries:");
          sessionWordBoundaries.slice(0, 10).forEach((b, i) => {
            logger.debug(
              `${i}: "${b.word}" (${b.offset}ms, duration: ${b.duration}ms)`,
            );
          });
        }

        // Возвращаем только сериализуемые данные для Redux
        return {
          audioLoaded: true,
          wordBoundariesCount: speechData.wordBoundaries?.length || 0,
        };
      } catch (error: unknown) {
        lastError = error;
        const info = classifyNetworkError(error);
        if (info.isConnectionError && attempt < maxRetries) {
          const delay = attempt * 2000;
          logger.warn(
            `[loadSessionAudio] Attempt ${attempt} failed (${info.code || info.message}), retrying in ${delay}ms...`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        logger.error(
          `[loadSessionAudio] Final failure after ${attempt} attempts`,
          error,
        );
        if (info.status === 403) {
          return rejectWithValue({
            message:
              info.message || i18n.t("dictation:errors.voiceAccessDenied"),
            status: 403,
            isVoiceAccessError: true,
          });
        }
        return rejectWithValue({
          message: info.message || i18n.t("dictation:errors.failedToLoadAudio"),
          status: info.status,
        });
      }
    }

    // Если все попытки неудачны
    logger.error("[loadSessionAudio] All retry attempts failed");
    const finalInfo = classifyNetworkError(lastError);
    return rejectWithValue(
      finalInfo.message ||
        i18n.t("dictation:errors.failedToLoadAudioAfterRetries"),
    );
  },
);

export const initializeAudioContext = createAsyncThunk(
  "dictation/initializeAudioContext",
  async (_, { rejectWithValue }) => {
    try {
      logger.debug("[initializeAudioContext] Pre-initializing AudioContext");
      const context = initializeAudioContextOnUserInteraction();
      return { initialized: true, state: context.state };
    } catch (error) {
      logger.error("[initializeAudioContext] Initialization error", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : i18n.t("dictation:errors.failedToInitializeAudiocontext"),
      );
    }
  },
);

// Modify playCurrentSentence thunk to use module-level audio session variables
export const playCurrentSentence = createAsyncThunk(
  "dictation/playCurrentSentence",
  async (_, { getState, dispatch, rejectWithValue }) => {
    const requestAudioVersion = audioVersion;
    try {
      const state = getState() as { dictation: DictationState };
      const { session, currentSentenceIndex } = state.dictation;
      const audioBuffer = sessionAudioBuffer;
      const wordBoundaries = sessionWordBoundaries;

      if (!session || !audioBuffer || !wordBoundaries) {
        logger.error("Missing required data", {
          session: !!session,
          audioBuffer: !!audioBuffer,
          wordBoundaries: !!wordBoundaries,
        });
        return rejectWithValue(
          i18n.t("dictation:errors.audioNotLoadedOrNoActiveSession"),
        );
      }
      logger.info("[playCurrentSentence] context", {
        currentSentenceIndex,
        totalSentences: session.sentences.length,
        sentence: session.sentences[currentSentenceIndex],
        currentWordBoundaryIndex,
        audioBufferBytes: audioBuffer ? audioBuffer.byteLength : null,
        wordBoundaries: wordBoundaries.length,
      });

      // Create or reuse AudioContext with iOS compatibility
      let context = sessionAudioContext;
      if (!context || !isAudioContextInitialized) {
        logger.debug(
          "[playCurrentSentence] Initializing AudioContext for first playback",
        );
        context = initializeAudioContextOnUserInteraction();
        sessionAudioContext = context;
      } else {
        logger.debug(
          "[playCurrentSentence] Using existing AudioContext",
          context.state,
        );
      }

      // Если контекст приостановлен, возобновляем его
      if (context.state === "suspended") {
        logger.debug(
          "[playCurrentSentence] AudioContext suspended, resuming...",
        );
        try {
          await context.resume();
          logger.debug(
            "[playCurrentSentence] AudioContext resumed",
            context.state,
          );
          isAudioContextInitialized = true;
        } catch (error) {
          logger.error(
            "[playCurrentSentence] Error resuming AudioContext",
            error,
          );
          return rejectWithValue(
            i18n.t("dictation:errors.failedToResumeAudiocontext"),
          );
        }
      }
      logger.debug("[playCurrentSentence] Decoding audio buffer...");
      // Декодируем аудио
      const decodedBuffer = await context.decodeAudioData(audioBuffer.slice(0));
      if (requestAudioVersion !== audioVersion)
        return rejectWithValue(
          i18n.t("dictation:errors.dictationExerciseChanged"),
        );
      logger.debug("[playCurrentSentence] Audio buffer decoded", {
        duration: decodedBuffer.duration,
        sampleRate: decodedBuffer.sampleRate,
        numberOfChannels: decodedBuffer.numberOfChannels,
      });

      // Устанавливаем правильную позицию поиска для текущего предложения
      setWordBoundaryPositionForSentence(
        currentSentenceIndex,
        session.sentences,
        wordBoundaries,
      );

      // Находим временные метки для текущего предложения
      const sentenceTimings = findSentenceTimings(
        session.sentences[currentSentenceIndex],
        wordBoundaries,
      );
      if (!sentenceTimings) {
        logger.warn(
          "Could not determine sentence timings, playing from beginning",
        );
        // Если не нашли временные метки, играем первые 3 секунды
        const fallbackTimings = { start: 0, end: 3 };
        return playAudioSegment(
          context,
          decodedBuffer,
          fallbackTimings,
          dispatch,
        );
      }

      logger.debug(
        `Playing segment: ${sentenceTimings.start.toFixed(2)}s - ${sentenceTimings.end.toFixed(2)}s`,
      );
      return playAudioSegment(
        context,
        decodedBuffer,
        sentenceTimings,
        dispatch,
      );
    } catch (error) {
      logger.error("Failed to play sentence", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : i18n.t("dictation:errors.failedToPlaySentence"),
      );
    }
  },
);

// Modify stopAudioPlayback thunk to use module-level source
export const stopAudioPlayback = createAsyncThunk(
  "dictation/stopAudioPlayback",
  async () => {
    logger.debug("[stopAudioPlayback] stopping all audio playback");

    // Stop Web Audio API source
    if (sessionAudioSource) {
      try {
        sessionAudioSource.stop();
        sessionAudioSource.disconnect();
      } catch (error) {
        logger.warn("Warning stopping Web Audio source", error);
      }
      sessionAudioSource = null;
    }

    // Stop fallback HTML5 audio
    if (fallbackAudioElement && !fallbackAudioElement.paused) {
      logger.debug("[stopAudioPlayback] stopping fallback audio");
      fallbackAudioElement.pause();
      // Don't reset currentTime to allow resume if needed
    }

    // Clear fallback timeout
    if (fallbackTimeoutId) {
      clearTimeout(fallbackTimeoutId);
      fallbackTimeoutId = null;
    }

    return { stopped: true };
  },
);

// Slice
const dictationSlice = createSlice({
  name: "dictation",
  initialState,
  reducers: {
    setRecommendationTextSize: (
      state,
      action: PayloadAction<DictationState["recommendationTextSize"]>,
    ): void => {
      state.recommendationTextSize = action.payload;
      state.recommendationRequestId = null;
      state.recommendationError = null;
    },
    restoreDraft: (state, action: PayloadAction<DictationDraft>): void => {
      const { elapsedTimeMs, ...draft } = action.payload;
      Object.assign(state, initialState, draft);
      state.userInput =
        action.payload.userInputs[action.payload.currentSentenceIndex] || "";
      state.settings = {
        ...defaultSettings,
        ...action.payload.session.settings,
      };
      state.status = DictationStatus.READY;
      state.startTime = Date.now() - elapsedTimeMs;
    },
    setUserInput: (state, action: PayloadAction<string>) => {
      // Avoid unnecessary updates if value hasn't changed
      if (state.userInput !== action.payload) {
        state.userInput = action.payload;
        state.userInputs[state.currentSentenceIndex] = action.payload;
      }
    },

    // Alias for compatibility with new interface
    updateUserInput: (state, action: PayloadAction<string>) => {
      // Avoid unnecessary updates if value hasn't changed
      if (state.userInput !== action.payload) {
        state.userInput = action.payload;
        state.userInputs[state.currentSentenceIndex] = action.payload;
      }
    },

    // Audio control actions
    setVolume: (state, action: PayloadAction<number>) => {
      state.volume = Math.max(0, Math.min(1, action.payload));
    },

    setCurrentTime: (state, action: PayloadAction<number>) => {
      state.currentTime = action.payload;
    },

    setAudioDuration: (state, action: PayloadAction<number>) => {
      state.audioDuration = action.payload;
    },

    nextSentence: (state) => {
      if (
        state.session &&
        state.currentSentenceIndex < state.session.sentences.length - 1
      ) {
        const oldIndex = state.currentSentenceIndex;
        state.currentSentenceIndex += 1;
        logger.debug("[nextSentence] advance", {
          from: oldIndex,
          to: state.currentSentenceIndex,
          total: state.session.sentences.length,
          text: state.session.sentences[state.currentSentenceIndex],
        });

        // восстанавливаем предыдущий ввод
        state.userInput = state.userInputs[state.currentSentenceIndex] || "";
        state.status = DictationStatus.READY;

        // Обновляем позицию поиска в wordBoundaries для нового предложения
        if (sessionWordBoundaries && state.session.sentences) {
          setWordBoundaryPositionForSentence(
            state.currentSentenceIndex,
            state.session.sentences,
            sessionWordBoundaries,
          );
        }
      } else {
        logger.debug(
          `[nextSentence] blocked: current=${state.currentSentenceIndex}, total=${state.session?.sentences?.length || 0}`,
        );
      }
    },

    previousSentence: (state) => {
      if (state.currentSentenceIndex > 0) {
        logger.debug(
          `[previousSentence] ${state.currentSentenceIndex} -> ${state.currentSentenceIndex - 1}`,
        );
        state.currentSentenceIndex -= 1;
        state.userInput = state.userInputs[state.currentSentenceIndex] || "";

        // Обновляем позицию поиска в wordBoundaries для нового предложения
        if (sessionWordBoundaries && state.session?.sentences) {
          setWordBoundaryPositionForSentence(
            state.currentSentenceIndex,
            state.session.sentences,
            sessionWordBoundaries,
          );
        }
      }
    },

    repeatCurrentSentence: (state) => {
      const currentIndex = state.currentSentenceIndex;
      // Этот action вызывается только для кнопки "repeat"
      // Логика подсчета повторов теперь в playCurrentSentence.fulfilled
      // Но отмечаем, что предложение воспроизводилось, чтобы следующий play считался повтором
      state.playedSentences[currentIndex] = true;
      logger.debug(
        `[repeatCurrentSentence] invoked for sentence ${currentIndex}`,
      );
    },

    setStatus: (state, action: PayloadAction<DictationStatus>) => {
      state.status = action.payload;
    },

    resetSession: (state) => {
      // Audio context handled via module variables
      // Clean up fallback audio
      if (fallbackAudioElement) {
        fallbackAudioElement.pause();
        fallbackAudioElement = null;
      }
      if (fallbackAudioBlob) {
        URL.revokeObjectURL(fallbackAudioBlob);
        fallbackAudioBlob = null;
      }

      state.status = DictationStatus.IDLE;
      state.session = null;
      state.currentSentenceIndex = 0;
      state.userInput = "";
      state.userInputs = [];
      state.sentenceRepeats = {};
      state.playedSentences = {};
      state.startTime = null;
      state.result = null;
      state.error = null;
      state.volume = 1;
      state.currentTime = 0;
      state.audioDuration = 0;
      state.audioLoaded = false; // Reset audio loading flag
      state.hasStarted = false; // Reset session start flag
      state.isPlayingAudio = false; // Reset audio playback flag
      currentWordBoundaryIndex = 0;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.status = DictationStatus.ERROR;
    },

    clearError: (state) => {
      state.error = null;
    },

    updateLocalSettings: (
      state,
      action: PayloadAction<Partial<DictationSettings>>,
    ) => {
      state.settings = { ...state.settings, ...action.payload };
      // Also save to localStorage for persistence
      localStorage.setItem("dictationSettings", JSON.stringify(state.settings));
    },

    loadLocalSettings: (state) => {
      try {
        const saved = localStorage.getItem("dictationSettings");
        if (saved) {
          state.settings = { ...defaultSettings, ...JSON.parse(saved) };
        }
      } catch (error) {
        logger.error("Failed to load local settings", error);
      }
    },

    resetDictationState: () => initialState,
  },

  extraReducers: (builder) => {
    builder.addCase(
      loadDictationRecommendation.pending,
      (state, action): void => {
        state.recommendationRequestId = action.meta.requestId;
        state.recommendationLoading = true;
        state.recommendationError = null;
      },
    );
    builder.addCase(
      loadDictationRecommendation.fulfilled,
      (state, action): void => {
        if (state.recommendationRequestId !== action.meta.requestId) return;
        state.recommendation = action.payload;
        state.recommendationLoading = false;
      },
    );
    builder.addCase(
      loadDictationRecommendation.rejected,
      (state, action): void => {
        if (state.recommendationRequestId !== action.meta.requestId) return;
        state.recommendationError =
          action.payload ?? action.error.message ?? "";
        state.recommendationLoading = false;
      },
    );
    // Initialize AudioContext (for iOS compatibility)
    builder
      .addCase(initializeAudioContext.pending, () => {
        logger.debug(
          "[initializeAudioContext.pending] Initializing AudioContext...",
        );
      })
      .addCase(initializeAudioContext.fulfilled, (_, action) => {
        logger.debug(
          "[initializeAudioContext.fulfilled] AudioContext initialized",
          action.payload,
        );
      })
      .addCase(initializeAudioContext.rejected, (_, action) => {
        logger.error(
          "[initializeAudioContext.rejected] AudioContext initialization error",
          action.payload,
        );
      });

    // Start session
    builder
      .addCase(startDictationSession.pending, (state) => {
        state.status = DictationStatus.LOADING;
        state.error = null;
      })
      .addCase(startDictationSession.fulfilled, (state, action) => {
        state.status = DictationStatus.READY;
        state.session = action.payload;
        state.currentSentenceIndex = 0;
        state.userInput = "";
        // Инициализируем массив пользовательских вводов
        state.userInputs = Array(action.payload.sentences.length).fill("");
        state.sentenceRepeats = {};
        state.playedSentences = {};
        state.startTime = Date.now();
        state.result = null;
        state.settings = { ...state.settings, ...action.payload.settings };
        state.audioLoaded = false; // Сбрасываем флаг загрузки аудио для новой сессии
        state.hasStarted = false; // Сессия создана, но еще не началась

        // Логируем все предложения для диагностики
        logger.debug("[startDictationSession] session sentences", {
          sentences: action.payload.sentences,
        });
      })
      .addCase(startDictationSession.rejected, (state, action) => {
        state.status = DictationStatus.ERROR;
        const raw = action.payload;
        let message: string | undefined;
        if (raw && typeof raw === "object") {
          const r = raw as {
            message?: unknown;
            data?: { message?: unknown; error?: unknown };
          };
          if (typeof r.message === "string") message = r.message;
          else if (r.data) {
            if (typeof r.data.message === "string") message = r.data.message;
            else if (typeof r.data.error === "string") message = r.data.error;
          }
        } else if (typeof raw === "string") {
          message = raw;
        }
        state.error =
          message ||
          action.error.message ||
          i18n.t("dictation:errors.failedToStartDictationSession");
      });

    // Submit dictation
    builder
      .addCase(submitDictation.pending, (state) => {
        state.status = DictationStatus.LOADING;
      })
      .addCase(submitDictation.fulfilled, (state, action) => {
        logger.debug("[submitDictation.fulfilled] result received", {
          result: action.payload,
          finalAccuracy: action.payload?.finalAccuracy,
          baseAccuracy: action.payload?.baseAccuracy,
          smartComparison: action.payload?.smartComparison,
        });

        state.status = DictationStatus.COMPLETED;
        state.result = action.payload;
      })
      .addCase(submitDictation.rejected, (state, action) => {
        state.status = DictationStatus.ERROR;
        state.error =
          action.error.message ||
          i18n.t("dictation:errors.failedToSubmitDictation");
      });

    // Load stats
    builder.addCase(loadDictationStats.fulfilled, (state, action) => {
      state.stats = action.payload;
    });

    // Load history
    builder.addCase(loadDictationHistory.fulfilled, (state, action) => {
      state.history = action.payload;
    });

    // Load settings
    builder.addCase(loadDictationSettings.fulfilled, (state, action) => {
      state.settings = { ...defaultSettings, ...action.payload };
    });

    // Update settings
    builder.addCase(updateDictationSettings.fulfilled, (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    });

    // Load session audio
    builder
      .addCase(loadSessionAudio.pending, (state) => {
        state.status = DictationStatus.LOADING;
      })
      .addCase(loadSessionAudio.fulfilled, (state, action) => {
        // Данные уже сохранены в модульных переменных внутри thunk
        // reset sequence search pointer for new audio session
        currentWordBoundaryIndex = 0;
        state.status = DictationStatus.READY;
        state.audioLoaded = true; // Устанавливаем флаг, что аудио загружено
        logger.debug(
          "Audio session loaded successfully",
          action.payload.wordBoundariesCount,
        );
      })
      .addCase(loadSessionAudio.rejected, (state, action) => {
        state.status = DictationStatus.ERROR;
        const raw = action.payload;
        let message: string | undefined;
        if (
          raw &&
          typeof raw === "object" &&
          "message" in (raw as Record<string, unknown>)
        ) {
          const m = (raw as { message?: unknown }).message;
          if (typeof m === "string") message = m;
        } else if (typeof raw === "string") {
          message = raw;
        }
        state.error = message || i18n.t("dictation:errors.failedToLoadAudio");
      });

    // Play current sentence
    builder
      .addCase(playCurrentSentence.pending, (state) => {
        state.status = DictationStatus.PLAYING;
        state.isPlayingAudio = true; // Block further playback requests
        state.hasStarted = true; // When playback starts, session is considered started
      })
      .addCase(playCurrentSentence.fulfilled, (state) => {
        const currentIndex = state.currentSentenceIndex;

        // If sentence was already played before, count this as a repeat
        if (state.playedSentences[currentIndex]) {
          state.sentenceRepeats[currentIndex] =
            (state.sentenceRepeats[currentIndex] || 0) + 1;
          logger.debug(
            `[playCurrentSentence] repeat counted for sentence ${currentIndex}, total repeats: ${state.sentenceRepeats[currentIndex]}`,
          );
        } else {
          // Mark sentence as played
          state.playedSentences[currentIndex] = true;
          logger.debug(
            `[playCurrentSentence] first play of sentence ${currentIndex}`,
          );
        }

        state.status = DictationStatus.WAITING_INPUT;
        state.isPlayingAudio = false; // Unblock playback
      })
      .addCase(playCurrentSentence.rejected, (state, action): void => {
        state.status = DictationStatus.ERROR;
        state.isPlayingAudio = false; // Unblock playback even on error
        state.error =
          (action.payload as string) ||
          i18n.t("dictation:errors.failedToPlaySentence");
      });

    // Stop audio playback
    builder.addCase(stopAudioPlayback.fulfilled, (state) => {
      if (state.status === DictationStatus.PLAYING) {
        state.status = DictationStatus.WAITING_INPUT;
      }
      state.isPlayingAudio = false; // Разблокируем воспроизведение при остановке
    });
  },
});

export const {
  setRecommendationTextSize,
  restoreDraft,
  setUserInput,
  updateUserInput,
  setVolume,
  setCurrentTime,
  setAudioDuration,
  nextSentence,
  previousSentence,
  repeatCurrentSentence,
  setStatus,
  resetSession,
  setError,
  clearError,
  updateLocalSettings,
  loadLocalSettings,
  resetDictationState,
} = dictationSlice.actions;

// Temporary action creators for new interface compatibility
export const seekAudio = (time: number): PayloadAction<number> =>
  setCurrentTime(time);
export const submitCurrentSentence = (): ((
  dispatch: (...args: unknown[]) => unknown,
  getState: () => unknown,
) => Promise<void>) => {
  return async (
    dispatch: (...args: unknown[]) => unknown,
    getState: () => unknown,
  ): Promise<void> => {
    logger.debug("submitCurrentSentence called");
    const state = getState() as { dictation: DictationState };
    const currentIndex = state.dictation.currentSentenceIndex;
    const session = state.dictation.session;
    const hasStarted = state.dictation.hasStarted;
    const status = state.dictation.status;

    // Блокируем выполнение во время воспроизведения
    if (status === DictationStatus.PLAYING) {
      logger.debug("submitCurrentSentence blocked - audio is playing");
      return;
    }

    logger.debug("submitCurrentSentence state", {
      currentIndex,
      totalSentences: session?.sentences?.length,
      userInputs: state.dictation.userInputs,
      hasStarted,
      status,
    });

    // Если сессия еще не началась, просто запускаем первое воспроизведение
    if (!hasStarted) {
      logger.debug("Starting first sentence playback");
      dispatch(playCurrentSentence());
      return;
    }

    if (session && currentIndex < session.sentences.length - 1) {
      logger.debug("Moving to next sentence");
      dispatch(nextSentence());
      dispatch(playCurrentSentence());
    } else {
      logger.debug("Last sentence - submitting dictation");
      // Last sentence - submit dictation
      const userInputs = state.dictation.userInputs;

      const timeSpent = state.dictation.startTime
        ? Date.now() - state.dictation.startTime
        : 0;

      logger.debug("Preparing submission data", {
        sessionId: session?.id,
        sessionIdValid: !!session?.id,
        userInputsCount: userInputs.length,
        combinedUserInput: userInputs.join(" "),
        combinedUserInputLength: userInputs.join(" ").length,
        timeSpent,
        sentenceRepeats: state.dictation.sentenceRepeats,
        wordBoundariesCount: sessionWordBoundaries?.length || 0,
      });

      // Validate required fields before submit
      if (!session?.id) {
        logger.error("Missing sessionId");
        return;
      }

      void dispatch(
        submitDictation({
          sessionId: session?.id || "",
          userInput: userInputs.join(" "), // Combined for legacy compatibility
          userInputsBySentence: userInputs, // Array of sentence strings
          timeSpentMs: timeSpent,
          sentenceRepeats: state.dictation.sentenceRepeats || {}, // Ensure object shape
        }),
      );
    }
  };
};

export default dictationSlice.reducer;
